import { FC } from 'react';
import { useLocation } from 'react-router';
import { ROUTES } from '../../constants/RouteNameMapping';
import { KpiCompaniesPreview } from './components/KPICompanies/KpiCompaniesPreview';
import { KpiRequestQueuePreview } from './components/KPIRequests/KpiRequestQueuePreview';
import { KpiModuleVariant, kpiPreviewScreen } from './kpi-preview-screen';
import { KpiTemplatePreview } from './KpiCollection/CustomKpiTemplate/KpiTemplatePreview';
import { KpiCollectionConfigPreview } from './KpiCollection/KpiCollectionConfigPreview';
import { KpiCompanySettingsPreview } from './KpiCollection/KpiCompanySettingsPreview';
import { NewTemplateId } from './KpiCollection/CustomKpiTemplate/CustomKpiTemplatePage';
import { NewKpiCollectionConfigId } from './KpiCollection/KpiCollectionConfigsPage';
import { CompanyPeriodRequestManagePreview } from './ManageRequests/CompanyPeriodRequestManagePreview';
import {
  useConsolidatedTemplateName,
  useKpiConfigurationName,
  useLegacyTemplateName,
} from './useKpiTemplateName';

// One wrapper per template store, so only the variant's own store is read.
const TemplatePreviewForRoute: FC<{ templateId?: string }> = ({ templateId }) => (
  <KpiTemplatePreview name={useConsolidatedTemplateName(templateId)} isNew={templateId === NewTemplateId} />
);

const LegacyTemplatePreviewForRoute: FC<{ templateId?: string }> = ({ templateId }) => (
  <KpiTemplatePreview name={useLegacyTemplateName(templateId)} />
);

const ConfigurationPreviewForRoute: FC<{ configId?: string }> = ({ configId }) => (
  <KpiCollectionConfigPreview
    name={useKpiConfigurationName(configId)}
    isNew={configId === NewKpiCollectionConfigId}
  />
);

interface KpiModulePreviewProps {
  variant: KpiModuleVariant;
}

/** The sample screen for the current KPI route, so the frost follows the sub-nav. */
export const KpiModulePreview: FC<KpiModulePreviewProps> = ({ variant }) => {
  const { pathname } = useLocation();
  const screen = kpiPreviewScreen(pathname);

  switch (screen.kind) {
    case 'companies':
      if (variant === 'consolidated') {
        return <KpiCompanySettingsPreview />;
      }
      return <KpiCompaniesPreview tab={screen.tab === ROUTES.ASSIGNED_COMPANIES ? 'assigned' : 'all'} />;
    case 'template':
      if (variant === 'consolidated') {
        return <ConfigurationPreviewForRoute configId={screen.tab} />;
      }
      return variant === 'legacy' ? (
        <LegacyTemplatePreviewForRoute templateId={screen.tab} />
      ) : (
        <TemplatePreviewForRoute templateId={screen.tab} />
      );
    case 'requests':
      return <KpiRequestQueuePreview kind='requests' tab={screen.tab} />;
    case 'responses':
      return <KpiRequestQueuePreview kind='responses' tab={screen.tab} />;
    default:
      return <CompanyPeriodRequestManagePreview />;
  }
};
